import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "./ui/use-toast";
import axios from "axios";
import { Search } from "lucide-react";

export default function AssignRsmModal({
  isOpen,
  onClose,
  clusterId,
  clusterName,
  onAssignComplete,
}) {
  const [rsms, setRsms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRsmId, setSelectedRsmId] = useState(null);
  const [assignLoading, setAssignLoading] = useState(false);

  // Fetch RSMs when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchRsms();
    }
  }, [isOpen]);

  // Function to fetch RSMs from backend
  const fetchRsms = async () => {
    setLoading(true);
    try {
      // This endpoint should return RSMs the BM can assign to
      const response = await axios.get("/users?role=RSM");
      setRsms(response.data);
    } catch (error) {
      console.error("Error fetching RSMs:", error);
      toast({
        title: "Could not load RSMs",
        description: error.response?.data?.error || error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter RSMs based on search query
  const filteredRsms = rsms.filter(
    (rsm) =>
      rsm.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      rsm.username?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // Assign the schedule to the selected RSM
  const handleAssign = async () => {
    if (!selectedRsmId) {
      toast({
        title: "No RSM Selected",
        description: "Please select an RSM to assign this schedule to.",
        variant: "destructive",
      });
      return;
    }

    setAssignLoading(true);
    try {
      // Check if this is a BYOI itinerary (starts with ITN-) or regular schedule
      const isByoiItinerary = clusterId && clusterId.toString().startsWith('ITN-');
      
      if (isByoiItinerary) {
        // Use BYOI-specific endpoint
        await axios.post(`/api/itineraries/byoi/${clusterId}/assign`, {
          rsm_id: selectedRsmId,
        });
      } else {
        // Use regular schedule endpoint
        await axios.post(`/schedules/${clusterId}/assign`, {
          rsm_id: selectedRsmId,
        });
      }

      toast({
        title: "Success!",
        description: "Successfully assigned to RSM",
      });

      // Notify parent component to refresh data
      if (onAssignComplete) onAssignComplete();

      // Close the modal
      onClose();
    } catch (error) {
      console.error("Error assigning:", error);
      toast({
        title: "Assignment Failed",
        description: error.response?.data?.error || error.message,
        variant: "destructive",
      });
    } finally {
      setAssignLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Assign Schedule to RSM</DialogTitle>
        </DialogHeader>

        <div className="py-4">
          <div className="mb-4">
            <h3 className="text-sm font-medium mb-1">Schedule:</h3>
            <p className="text-sm bg-muted p-2 rounded">
              {clusterName || `Schedule ${clusterId}`}
            </p>
          </div>

          <div className="mb-4">
            <h3 className="text-sm font-medium mb-1">Search RSMs:</h3>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or username..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>

          <div className="mb-4">
            <h3 className="text-sm font-medium mb-2">Select RSM:</h3>
            {loading ? (
              <div className="text-center py-6">Loading RSMs...</div>
            ) : filteredRsms.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground">
                {searchQuery
                  ? "No RSMs match your search"
                  : "No RSMs available"}
              </div>
            ) : (
              <div className="max-h-60 overflow-y-auto space-y-2">
                {filteredRsms.map((rsm) => (
                  <div
                    key={rsm.id}
                    className={`flex items-center p-2 rounded cursor-pointer ${
                      selectedRsmId === rsm.id
                        ? "bg-primary text-primary-foreground"
                        : "hover:bg-muted"
                    }`}
                    onClick={() => setSelectedRsmId(rsm.id)}
                  >
                    <div className="flex-1 truncate">
                      <div className="font-medium">{rsm.full_name}</div>
                      <div className="text-xs text-muted-foreground">
                        {selectedRsmId === rsm.id ? "Selected" : rsm.username}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleAssign}
            disabled={!selectedRsmId || assignLoading}
          >
            {assignLoading ? "Assigning..." : "Assign Schedule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
